import { useEffect, useState } from "react";
import {
  Container,
  Paper,
  Grid,
  Box,
  Typography,
  Button,
  CircularProgress,
  Stack,
  Chip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { getAdvertisingBanners } from "../../../services/Admin/AdvertisingBannersApi";
import AddAdvertisingBannerDialog from "./AddAdvertisingBannerDialog";
import EditAdvertisingBannerDialog from "./EditAdvertisingBannerDialog";
import DeleteAdvertisingBannerDialog from "./DeleteAdvertisingBannerDialog";
import { MEDIA_BASE_URL } from "../../../services/indexMediaBaseUrl";
import AddCategoriesToBannerDialog from "./AddCategoriesToBannerDialog";

const AdvertisingBannersPage = () => {
  const [loading, setLoading] = useState(false);
  const [banners, setBanners] = useState([]);
  const [error, setError] = useState("");
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [bannerToDelete, setBannerToDelete] = useState(null);
  const [openAddCategoriesDialog, setOpenAddCategoriesDialog] = useState(false);
  const [bannerForCategories, setBannerForCategories] = useState(null);

  const fetch = async () => {
    setError("");
    setLoading(true);
    try {
      const res = await getAdvertisingBanners();
      const data = res?.success ? res.data : res;
      setBanners(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || "فشل تحميل الإعلانات");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetch();
  }, []);

  // عرض التاريخ بصيغة 12 ساعة (مرن مع ISO/UTC)
  const formatDate12 = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(d);
  };

  // helper: return active label localized and keep old helper name format
  const isActiveLabel = (v) => (Number(v) === 1 ? "مفعّل" : "معطّل");

  // backward-compatible formatDate used elsewhere -> delegate to formatDate12
  const formatDate = (v) => formatDate12(v);

  const imgUrl = (p) => (p ? `${(MEDIA_BASE_URL || "").replace(/\/$/, "") ? `${MEDIA_BASE_URL.replace(/\/$/, "")}/` : ""}${p}` : null);

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h6">البانرات الإعلانية</Typography>
            <Typography variant="body2" color="text.secondary">عرض جميع الإعلانات</Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button startIcon={<AddIcon />} onClick={() => setOpenAddDialog(true)} disabled={loading} size="small" variant="contained">
              إضافة بانر
            </Button>
            <Button startIcon={<RefreshIcon />} onClick={fetch} disabled={loading} size="small" variant="outlined">
              {loading ? "جارٍ..." : "تحديث"}
            </Button>
          </Stack>
        </Stack>

        {loading ? (
          <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : !banners.length ? (
          <Typography color="text.secondary">لا توجد إعلانات</Typography>
        ) : (
          <Grid container spacing={2}>
            {banners.map((b) => (
              <Grid item xs={12} sm={6} md={4} key={b.id}>
                <Paper sx={{ p: 2, height: "100%" }} variant="outlined">
                  <Stack spacing={1}>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", justifyContent: "space-between" }}>
                      <Box sx={{ height: 160, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "grey.100", borderRadius: 1, overflow: "hidden", gap: 1, flex: 1 }}>
                        {b.image || b.image_web ? (
                          <Stack direction="row" spacing={1} sx={{ width: "100%", justifyContent: "center", alignItems: "center" }}>
                            {b.image && (
                              <Box sx={{ textAlign: "center", width: b.image_web ? "50%" : "100%" }}>
                                <Typography variant="caption" color="text.secondary">image</Typography>
                                <Box
                                  component="img"
                                  src={imgUrl(b.image)}
                                  alt={b.title_ar || b.title_en || "banner-mobile"}
                                  sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                                />
                              </Box>
                            )}
                            {b.image_web && (
                              <Box sx={{ textAlign: "center", width: b.image ? "50%" : "100%" }}>
                                <Typography variant="caption" color="text.secondary">image_web</Typography>
                                <Box
                                  component="img"
                                  src={imgUrl(b.image_web)}
                                  alt={b.title_ar || b.title_en || "banner-web"}
                                  sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                                />
                              </Box>
                            )}
                          </Stack>
                        ) : (
                          <Typography color="text.secondary">لا توجد صورة</Typography>
                        )}
                      </Box>
                      <Box sx={{ width: 36 }}>
                        <Button
                          size="small"
                          onClick={() => {
                            setSelectedBanner(b);
                            setOpenEditDialog(true);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </Button>
                      </Box>
                      <Box sx={{ width: 36 }}>
                        <Button
                          size="small"
                          onClick={() => {
                            setBannerToDelete(b);
                            setOpenDeleteDialog(true);
                          }}
                        >
                          <DeleteIcon fontSize="small" color="error" />
                        </Button>
                      </Box>
                    </Box>

                    <Box>
                      <Typography sx={{ fontWeight: 700 }}>{b.title_ar || b.title_en || "بدون عنوان"}</Typography>
                      <Typography variant="caption" color="text.secondary">{b.title_en || ""}</Typography>
                    </Box>

                    <Typography variant="body2" color="text.secondary" sx={{ minHeight: 40 }}>
                      {b.description_ar || b.description_en || "-"}
                    </Typography>

                    {/* تفاصيل الصورة والتواريخ */}
                    <Stack spacing={0.5} sx={{ pt: 1 }}>
                      <Typography variant="caption" color="text.secondary">image: {b.image ?? "-"}</Typography>
                      <Typography variant="caption" color="text.secondary">image_web: {b.image_web ?? "-"}</Typography>
                      <Typography variant="caption" color="text.secondary">تاريخ الانشاء: {formatDate(b.created_at)}</Typography>
                      <Typography variant="caption" color="text.secondary">تاريخ التحديث: {formatDate(b.updated_at)}</Typography>
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center" sx={{ pt: 1, flexWrap: "wrap" }}>
                      <Chip label={isActiveLabel(b.is_active)} size="small" color={Number(b.is_active) === 1 ? "success" : "default"} />
                      <Typography variant="caption" color="text.secondary">الترتيب: {b.display_order ?? "-"}</Typography>
                      <Typography variant="caption" color="text.secondary">من: {formatDate(b.start_date)}</Typography>
                      <Typography variant="caption" color="text.secondary">إلى: {formatDate(b.end_date)}</Typography>
                    </Stack>

                    <Box sx={{ pt: 1 }}>
                      <Typography variant="subtitle2">التصنيفات</Typography>
                      {Array.isArray(b.categories) && b.categories.length ? (
                        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                          {b.categories.map((c) => (
                            <Chip key={c.id} label={c.name_ar || c.name_en} size="small" />
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="caption" color="text.secondary">لا توجد تصنيفات</Typography>
                      )}
                      {/* زر إضافة تصنيفات */}
                      <Box sx={{ mt: 1 }}>
                        <Button
                          size="small"
                          onClick={() => {
                            setBannerForCategories(b);
                            setOpenAddCategoriesDialog(true);
                          }}
                        >
                          إضافة تصنيفات
                        </Button>
                      </Box>
                    </Box>


                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>
      <AddAdvertisingBannerDialog
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
        onSuccess={() => {
          setOpenAddDialog(false);
          fetch();
        }}
      />
      <EditAdvertisingBannerDialog
        open={openEditDialog}
        onClose={() => {
          setOpenEditDialog(false);
          setSelectedBanner(null);
        }}
        banner={selectedBanner}
        onSuccess={() => {
          setOpenEditDialog(false);
          setSelectedBanner(null);
          fetch();
        }}
      />

      <DeleteAdvertisingBannerDialog
        open={openDeleteDialog}
        onClose={() => {
          setOpenDeleteDialog(false);
          setBannerToDelete(null);
        }}
        banner={bannerToDelete}
        onSuccess={() => {
          setOpenDeleteDialog(false);
          setBannerToDelete(null);
          fetch();
        }}
      />

      <AddCategoriesToBannerDialog
        open={openAddCategoriesDialog}
        onClose={() => {
          setOpenAddCategoriesDialog(false);
          setBannerForCategories(null);
        }}
        bannerId={bannerForCategories?.id}
        existingCategoryIds={Array.isArray(bannerForCategories?.categories) ? bannerForCategories.categories.map(c=>c.id) : []}
        onSuccess={() => {
          setOpenAddCategoriesDialog(false);
          setBannerForCategories(null);
          fetch();
        }}
      />
    </Container>
  );
};

export default AdvertisingBannersPage;
